import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeDrivers } from 'Core/hooks';
import { useIntl } from 'react-intl';
import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

import { v4 as uuidv4 } from 'uuid';

import {
    Modal,
    IconButton,
    Label,
    Button
} from '@gpstrackit/gtc-react-controls';

import { Timezone, getDateTime } from 'Components/uiControls';
import { convertImageToBase64ForPDF } from 'Core/data/Helpers';

import { Alert, Grid, Typography } from '@mui/material';
import { fieldTypes } from '../../.globals/helpers';

//actions
import {
    reset,
    getDynamicFormAnswers,
    closeViewAnswersModal
} from './redux/slice';

import AnswersAdvancedFilters from './components/advancedFilters';

import './styles.scss';

applyPlugin(jsPDF);

const ViewAnswers = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const answersContentRef = useRef(null);
    const downloadButtonRef = useRef(null);
    const titleRef = useRef(null);

    const open = useSelector(state => state.dynamicFormsViewAnswersRedux.open);
    const answers = useSelector(state => state.dynamicFormsViewAnswersRedux.answers);
    const loading = useSelector(state => state.dynamicFormsViewAnswersRedux.loading);
    const id = useSelector(state => state.dynamicFormsViewAnswersRedux.id);
    const error = useSelector(state => state.dynamicFormsViewAnswersRedux.error);

    const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
    const user = useSelector((state) => state.securityRedux.user);

    const { dateformat, timeZone } = user;

    const [errorMessage, setErrorMessage] = useState("");
    const [openImagePreview, setOpenImagePreview] = useState(false);
    const [failedImages, setFailedImages] = useState(new Set());

    const driver = drivers?.find(d => `DRIVER_${d.driverId}` == answers?.updatedBy);

    useEffect(() => {
        if (open && id) {
            dispatch(getDynamicFormAnswers({ id }));
        }
    }, [open, id]);

    useEffect(() => {
        if (open && error) {
            setErrorMessage(messages['errorMessage']);
            dispatch(reset({ error: false, answers: null }));
        }
    }, [error, open]);



    const exportToPDF = async () => {
        const unit = 'px';
        const size = 'A4';
        const orientation = 'portrait';

        const doc = new jsPDF(orientation, unit, size);
        doc.setFontSize(12);
        doc.setLineWidth(2);

        let answerDate = getDateTime({
            date: answers?.updatedOn,
            dateFormat: dateformat,
            timeFormat: "hh:mm a",
            timezone: timeZone
        });

        doc.text(30, 30, `${messages['dynamicFormsViewAnswers_form']}: ${answers?.dynamicForm?.name ? `"${answers?.dynamicForm?.name?.slice(0, 90)}"` : ""}`);
        doc.text(30, 45, `${messages['dynamicFormsViewAnswers_updatedAt']}: ${answerDate?.date}, ${answerDate?.abbr}`);
        doc.text(240, 45, `${messages['dynamicFormsViewAnswers_updatedBy']}: ${driver?.name || ""}`);

        let dataItems = [];
        for (let i = 0; i < answers?.dynamicForm?.pages?.length; i++) {
            const page = answers?.dynamicForm?.pages[i];
            dataItems.push([{
                content: page?.title || "",
                type: "Title",
                styles: {
                    textColor: 'black',
                    fontSize: 14,
                    fillColor: 'white',
                    cellWidth: 'auto',
                    fontStyle: 'bold',
                    overflow: 'linebreak',
                    minCellWidth: 50,
                    cellPadding: 0
                }
            }]);

            for (let j = 0; j < page?.fields?.length; j++) {
                const field = page?.fields[j];
                const answer = answers?.formValues?.find(value => value?.id === field?.id);

                let label = field?.label || "";
                let content = answer?.value || " --- ";
                if ([fieldTypes.Picture, fieldTypes.Signature]?.includes(answer?.type)) {
                    dataItems.push([{ content: label, type: "Label", styles: { cellWidth: 'auto', fontStyle: 'bold', overflow: 'linebreak', minCellWidth: 50 } }]);
                    if (answer?.value) {
                        try {
                            // Use the new utility function to convert presigned URL to base64
                            // Smaller dimensions but with high quality rendering
                            const imageData = await convertImageToBase64ForPDF(answer?.value, 120, 90);

                            dataItems.push([{
                                content: "",
                                imgUrl: imageData.dataUrl || imageData.imageUrl,
                                type: answer?.type,
                                width: imageData.width,
                                height: imageData.height,
                                isPlaceholder: imageData.isPlaceholder,
                                isOriginalUrl: imageData.isOriginalUrl,
                                isRealImage: imageData.isRealImage,
                                styles: { cellWidth: 'auto', overflow: 'linebreak', minCellWidth: 50, minCellHeight: imageData.height }
                            }]);
                        } catch (e) {
                            console.error('Error loading image for PDF:', e);
                            dataItems.push([{ content: `[${messages['image_load_error'] || 'Image could not be loaded'}]` }]);
                        }
                    } else {
                        dataItems.push([{ content: "" }]);
                    }
                } else if ([fieldTypes.DateTime, fieldTypes.Date, fieldTypes.Time].includes(answer?.type)) {
                    let newDate = getDateTime({
                        date: content,
                        dateFormat: dateformat,
                        timeFormat: (answer?.type === fieldTypes.DateTime || answer?.type === fieldTypes.Time) ? "hh:mm a" : " ",
                        timezone: timeZone,
                        onlyHour: (answer?.type === fieldTypes.Time ? true : false)
                    });

                    if (answer?.type === fieldTypes.DateTime) {
                        content = `${newDate?.date}, ${newDate?.abbr}`;
                    } else if (answer?.type === fieldTypes.Date) {
                        content = `${newDate?.date}, ${newDate?.abbr}`;
                    } else {
                        content = `${newDate?.date}, ${newDate?.abbr}`;
                    }

                    dataItems.push([{ content: label, type: "Label", styles: { cellWidth: 'auto', fontStyle: 'bold', overflow: 'linebreak', minCellWidth: 50 } }]);
                    dataItems.push([{ content: content, type: answer?.type, styles: { cellWidth: 'auto', overflow: 'linebreak', minCellWidth: 50 } }]);
                } else if ([fieldTypes.Switch].includes(answer?.type)) {
                    content = answer?.value === "true" ? field?.attributes?.onLabel : field?.attributes?.offLabel;
                    dataItems.push([{ content: label, type: "Label", styles: { cellWidth: 'auto', fontStyle: 'bold', overflow: 'linebreak', minCellWidth: 50 } }]);
                    dataItems.push([{ content: content, type: answer?.type, styles: { cellWidth: 'auto', overflow: 'linebreak', minCellWidth: 50 } }]);
                } else {
                    dataItems.push([{ content: label, type: "Label", styles: { cellWidth: 'auto', fontStyle: 'bold', overflow: 'linebreak', minCellWidth: 50 } }]);
                    dataItems.push([{ content: content, type: answer?.type, styles: { cellWidth: 'auto', overflow: 'linebreak', minCellWidth: 50 } }]);
                }
            }

            dataItems.push([{ content: "", type: "", styles: { cellWidth: 'auto', fillColor: 'white', cellPadding: 5 } }]);
        }

        let content = {
            startY: 60,
            rowPageBreak: "avoid",
            head: [],
            columnStyles: {},
            didDrawCell: (data) => {
                const item = data?.row?.raw[0];
                if (["Picture", "Signature"]?.includes(item?.type) && item?.imgUrl) {
                    try {
                        if (item.isRealImage) {
                            // Successfully loaded real image as base64
                            doc.addImage(item?.imgUrl, data?.cell?.x, data?.cell?.y, item?.width, item?.height);
                        } else if (item.isOriginalUrl) {
                            // Use original URL with jsPDF
                            const imageFormat = item?.imgUrl.toLowerCase().includes('.png') ? 'PNG' : 'JPEG';
                            doc.addImage(item?.imgUrl, imageFormat, data?.cell?.x, data?.cell?.y, item?.width, item?.height);
                        } else if (item.isPlaceholder) {
                            // Draw a styled placeholder for images
                            const x = data?.cell?.x + 2;
                            const y = data?.cell?.y + 5;
                            const width = item?.width - 4;
                            const height = item?.height - 2;

                            // Draw border
                            doc.setDrawColor(150, 150, 150);
                            doc.setLineWidth(1);
                            doc.rect(x, y, width, height);

                            // Add image icon and text
                            doc.setFontSize(10);
                            doc.setTextColor(100, 100, 100);

                            const imageType = item?.type === "Picture" ? messages['picture'] || 'Picture' : messages['signature'] || 'Signature';
                            const text = `📷 ${imageType}`;

                            // Center the text
                            const textWidth = doc.getTextWidth(text);
                            const textX = x + (width - textWidth) / 2;
                            const textY = y + height / 2 + 3;

                            doc.text(text, textX, textY);

                            // Add note about image availability
                            doc.setFontSize(8);
                            doc.setTextColor(120, 120, 120);
                            const noteText = messages['image_available'] || 'Image available in digital version';
                            const noteWidth = doc.getTextWidth(noteText);
                            const noteX = x + (width - noteWidth) / 2;
                            const noteY = textY + 10;

                            if (noteY < y + height - 2) {
                                doc.text(noteText, noteX, noteY);
                            }

                            // Reset colors
                            doc.setTextColor(0, 0, 0);
                        }
                    } catch (imageError) {
                        console.error('Error adding image to PDF:', imageError);
                        // Draw error placeholder
                        const x = data?.cell?.x + 2;
                        const y = data?.cell?.y + 5;
                        const width = item?.width - 4;
                        const height = item?.height - 2;

                        doc.setDrawColor(200, 100, 100);
                        doc.setLineWidth(1);
                        doc.rect(x, y, width, height);
                        doc.setFontSize(8);
                        doc.setTextColor(200, 100, 100);
                        doc.text('[Image Error]', x + 2, y + height / 2);
                        doc.setTextColor(0, 0, 0);
                    }
                }
            },
            body: dataItems,
        };

        doc.autoTable(content);
        doc.save(`${answers?.dynamicForm?.name?.slice(0, 30)}-${uuidv4()}`);
    };


    const handleClose = useCallback(() => {
        setErrorMessage("");
        setFailedImages(new Set());
        dispatch(closeViewAnswersModal());
        dispatch(reset());
    });

    const renderAnswer = useCallback((field, answer, index) => {
        let answerValue = " --- ";

        if (answer?.value) {
            switch (field?.type) {
                case fieldTypes?.Text:
                case fieldTypes?.TextArea:
                case fieldTypes?.Email:
                case fieldTypes?.Phone:
                case fieldTypes?.Number:
                    answerValue = answer?.value;
                    break;

                case fieldTypes?.Switch:
                    answerValue = answer?.value === "true" ? field?.attributes?.onLabel : field?.attributes?.offLabel;
                    break;

                case fieldTypes?.Checkbox:
                    try {
                        answerValue = answer?.value ? JSON.parse(answer?.value) : [];
                        answerValue = answerValue?.join(", ");
                    } catch (e) {
                        console.log(e);
                        answerValue = answer?.value;
                    }
                    break;

                case fieldTypes?.Picture:
                case fieldTypes?.Signature:
                    answerValue = (
                        <div>
                            <div className='image-answer-container'>
                                <img
                                    src={answer?.value}
                                    alt={field?.type === fieldTypes?.Picture ? (messages['picture'] || 'Picture') : (messages['signature'] || 'Signature')}
                                    className="image-answer"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        const container = e.target.parentElement;
                                        if (container && !container.querySelector('.image-error-placeholder')) {
                                            const placeholder = document.createElement('span');
                                            placeholder.className = 'image-error-placeholder';
                                            placeholder.textContent = messages['dynamicFormsViewAnswers_imageNotAvailable'];
                                            container.insertBefore(placeholder, e.target);

                                            // Marcar esta imagen como fallida
                                            setFailedImages(prev => new Set(prev).add(answer?.value));
                                        }
                                    }}
                                />

                                {!failedImages.has(answer?.value) && (
                                    <div className='image-answer-view-button'>
                                        <IconButton
                                            onClick={() => setOpenImagePreview(answer?.value)}
                                            icon="visibility"
                                            size='large'
                                            style={{
                                                color: "black"
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                    break;

                case fieldTypes?.DateTime:
                    answerValue = (
                        <Timezone
                            date={answer?.value}
                            dateFormat={dateformat}
                            timeFormat="hh:mm a"
                            timezone={timeZone}
                        />
                    );
                    break;

                case fieldTypes?.Date:
                    answerValue = (
                        <Timezone
                            date={answer?.value}
                            dateFormat={dateformat}
                            timeFormat=" "
                            timezone={timeZone}
                        />
                    );
                    break;

                case fieldTypes?.Time:
                    answerValue = (
                        <Timezone
                            date={answer?.value}
                            dateFormat={dateformat}
                            timeFormat="hh:mm a"
                            timezone={timeZone}
                            onlyHour
                        />
                    );
                    break;

                default:
                    answerValue = answer?.value;
                    break;
            }
        }

        return (
            <div
                className='answer-container'
                key={index}
            >
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    wrap="nowrap"
                >
                    <Grid item xs>
                        <Label style={{ fontSize: 14 }}>
                            {field?.label}
                        </Label>
                    </Grid>
                </Grid>

                <Grid
                    container
                    wrap="nowrap"
                >
                    <Grid item xs>
                        <Typography style={{ wordWrap: "break-word" }}>{answerValue}</Typography>
                    </Grid>
                </Grid>
            </div>
        )
    });

    return (
        <div>
            <Modal
                title={`${messages['dynamicFormsViewAnswers_form']}: ${answers?.dynamicForm?.name ? `"${answers?.dynamicForm?.name}"` : ""}`}
                open={open}
                loading={loading}
                position="right"
                size="large"
                handleClose={handleClose}
                footer={(
                    <div style={{ display: 'flex', alignItems: "center", justifyContent: 'end', width: '100%' }}>
                        <Button
                            disabled={loading}
                            style={{ marginRight: 8 }}
                            onClick={handleClose}
                            variant='secondary'
                        >
                            {messages['close']}
                        </Button>
                    </div>
                )}
            >
                <div
                    id='my-table'
                    ref={answersContentRef}
                    className='view-answers-container'
                >
                    {
                        errorMessage &&
                        <div>
                            <Alert severity="error">{errorMessage}</Alert>
                        </div>
                    }

                    {
                        answers &&
                        <>
                            <h3
                                style={{
                                    marginTop: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    listStylePosition: "inside",
                                    whiteSpace: "nowrap"
                                }}
                                ref={titleRef}
                            >
                                {answers?.dynamicForm?.name}
                            </h3>

                            <Grid
                                container
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item xs>
                                    <div style={{ fontWeight: 'bold', display: 'inline-block', paddingRight: 5 }}>
                                        {messages['dynamicFormsViewAnswers_updatedAt']}:
                                    </div>
                                    <div style={{ display: 'inline-block' }}>
                                        <Timezone
                                            date={answers?.updatedOn}
                                            dateFormat={dateformat}
                                            timeFormat="hh:mm a"
                                            timezone={timeZone}
                                        />
                                    </div>
                                </Grid>

                                <Grid item xs>
                                    <span style={{ fontWeight: 'bold' }}>{messages['dynamicFormsViewAnswers_updatedBy']}: </span>
                                    <span>
                                        {driver?.name || ""}
                                    </span>
                                </Grid>

                                <Grid item ref={downloadButtonRef}>
                                    <IconButton
                                        icon="download"
                                        tooltip={messages["download"]}
                                        placement="bottom"
                                        variant="secondary"
                                        onClick={exportToPDF}
                                    />
                                </Grid>
                            </Grid>

                            {
                                answers?.dynamicForm?.pages?.map((page, index) => {
                                    return (
                                        <div key={`${index}-${page?.id}`}>
                                            <Grid
                                                container
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                wrap="nowrap"
                                                style={{
                                                    marginTop: 14,
                                                    marginBottom: 5
                                                }}
                                            >
                                                <Grid item xs zeroMinWidth>
                                                    <Typography id={page?.id} variant='subtitle1' style={{ fontWeight: 'bold' }} noWrap>
                                                        {page?.title}
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            {
                                                page?.fields?.map((field, key) => {
                                                    const answer = answers?.formValues?.find(value => value?.id === field?.id)
                                                    return renderAnswer(field, answer, `${key}-${field?.id}`);
                                                })
                                            }
                                        </div>
                                    );
                                })
                            }
                        </>
                    }
                </div>

                <AnswersAdvancedFilters />
            </Modal>

            <Modal
                title={" "}
                open={openImagePreview ? true : false}
                position='right'
                size="full-screen"
                handleClose={() => setOpenImagePreview(null)}
            >
                <div
                    className='view-answers-container'
                    style={{
                        textAlign: "center"
                    }}
                >
                    <img
                        src={openImagePreview}
                        style={{ width: "auto", height: "auto" }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                            const container = e.target.parentElement;
                            if (container && !container.querySelector('.image-preview-error')) {
                                const placeholder = document.createElement('span');
                                placeholder.className = 'image-preview-error';
                                placeholder.textContent = 'Imagen no disponible';
                                container.appendChild(placeholder);
                            }
                        }}
                    />
                </div>
            </Modal>
        </div>
    );
}

export default ViewAnswers;